import React, { useMemo, useState } from "react";
import { Collapse } from "@mui/material";
import DashboardFilters from "./DashboardFilters";
import DashboardCardSection from "./DashboardCardSection";
import DashboardCharts from "./DashboardCharts";
import DashboardTeamsTable from "./DashboardTeamsTable";
import DashboardAgentCallReports from "./DashboardAgentCallReports";
import DashboardMonthlySalesTable from "./DashboardMonthlySalesTable";
import {
  SECTION_ORDER,
  sectionHeading,
  isLeadStatusItem,
  orderPaymentMetric,
} from "./dashboardConfig";

/** Blocks that are tables/charts in their own right, not card lists. */
const STRUCTURED_KEYS = new Set([
  "teams",
  "monthlySales",
  "agentCallReport",
  "reports",
]);

const isCardBlock = (key, value) =>
  !STRUCTURED_KEYS.has(key) &&
  Array.isArray(value) &&
  value.every((item) => item && typeof item === "object" && "title" in item);

/** Numbers already shown elsewhere (the Total Users card). */
const SCALAR_SKIP = new Set([
  "totalUsers",
  "total_active_users",
  "total_users",
]);

/**
 * Items a chart can plot, or `undefined` when the response has nothing for it.
 * A block counts if it is empty (shows the chart's empty state) or holds items the chart plots —
 * a block that merely has a matching name but nothing plottable does not bring the chart in.
 */
const collectItems = (data, keys, re, isPlottable) => {
  const blocks = keys
    .filter((k) => re.test(k))
    .map((k) => data[k].map((item) => ({ ...item, sectionKey: k })))
    .filter((items) => items.length === 0 || items.some(isPlottable));
  return blocks.length ? blocks.flat() : undefined;
};

/**
 * The one dashboard for every role. It renders whatever blocks the API returned —
 * the backend already scopes them to the logged-in user — so there is no role logic here.
 * The team filter and per-team table only appear when the payload carries `teams`.
 */
export default function DashboardView({
  data,
  loading,
  showFilters,
  dateFrom,
  dateTo,
  teamFilterId,
  teamOptions,
  teamsLoading,
  onDateFromChange,
  onDateToChange,
  onTeamChange,
  onTeamOpen,
  onApply,
  onClearFilters,
}) {
  const [teamSearch, setTeamSearch] = useState("");
  const [teamPage, setTeamPage] = useState(0);
  const [teamRowsPerPage, setTeamRowsPerPage] = useState(10);

  const hasTeams = Array.isArray(data.teams);

  // Every `[{ title, value }]` block the API sent becomes a card section — no per-role list.
  const cardKeys = useMemo(() => {
    const rank = (k) => {
      const i = SECTION_ORDER.indexOf(k);
      return i < 0 ? SECTION_ORDER.length : i;
    };
    return Object.keys(data)
      .filter((k) => isCardBlock(k, data[k]))
      .map((key, index) => ({ key, index }))
      .sort((a, b) => rank(a.key) - rank(b.key) || a.index - b.index)
      .map(({ key }) => key);
  }, [data]);
  // Plain top-level counts (`total_leads`, `overdue`…) become one "Summary" block of cards.
  const scalarItems = useMemo(
    () =>
      Object.entries(data)
        .filter(([k, v]) => typeof v === "number" && !SCALAR_SKIP.has(k))
        .map(([k, value]) => ({ title: sectionHeading(k), value })),
    [data],
  );
  const leadsItems = useMemo(
    () => collectItems(data, cardKeys, /lead/i, isLeadStatusItem),
    [data, cardKeys],
  );
  const orderItems = useMemo(
    () => collectItems(data, cardKeys, /order|sales/i, orderPaymentMetric),
    [data, cardKeys],
  );
  const usersSection = useMemo(
    () =>
      data.totalUsers != null
        ? [{ title: "Total Users", value: data.totalUsers }]
        : undefined,
    [data.totalUsers],
  );

  return (
    <>
      <Collapse in={Boolean(showFilters)} unmountOnExit>
        <DashboardFilters
          dateFrom={dateFrom}
          dateTo={dateTo}
          teamFilterId={teamFilterId}
          teams={teamOptions}
          onDateFromChange={onDateFromChange}
          onDateToChange={onDateToChange}
          onTeamChange={onTeamChange}
          onApply={onApply}
          onClearFilters={onClearFilters}
          onTeamOpen={onTeamOpen}
          teamsLoading={teamsLoading}
          showTeam={hasTeams}
        />
      </Collapse>

      <DashboardCardSection
        heading="Summary"
        items={scalarItems.length ? scalarItems : undefined}
        loading={loading}
      />
      {cardKeys.map((key) => (
        <DashboardCardSection
          key={key}
          sectionKey={key}
          heading={sectionHeading(key)}
          items={data[key]}
          loading={loading}
        />
      ))}
      <DashboardCardSection
        heading="Total Users"
        items={usersSection}
        loading={loading}
      />

      <DashboardCharts
        teams={data.teams}
        leadsSummary={leadsItems}
        sales={orderItems}
      />

      {hasTeams && (
        <DashboardTeamsTable
          teamsMetrics={data.teams}
          loading={loading}
          search={teamSearch}
          onSearchChange={setTeamSearch}
          page={teamPage}
          rowsPerPage={teamRowsPerPage}
          onPageChange={setTeamPage}
          onRowsPerPageChange={(v) => {
            setTeamRowsPerPage(v);
            setTeamPage(0);
          }}
        />
      )}
      <DashboardAgentCallReports agentCallReport={data.agentCallReport} />
      {Array.isArray(data.monthlySales) && (
        <DashboardMonthlySalesTable
          monthlySales={data.monthlySales}
          loading={loading}
        />
      )}
    </>
  );
}
